// Query keys and shared queries (session, shell counters). Feature queries live in features/*/api.ts.
import { queryOptions } from '@tanstack/react-query';
import { api, unwrap } from './client';
import type { SnapshotOut } from './research';
import type { Dashboard, Organization, OrganizationSummary, Plan, Run, Website } from './types';

export type { Me } from './types';

export const qk = {
  me: ['auth', 'me'] as const,
  dashboard: (period = 'weeks') => ['dashboard', period] as const,
  runningJobs: ['jobs', 'running'] as const,
  job: (id: string) => ['jobs', 'one', id] as const,
  organizations: (params: Record<string, unknown> = {}) => ['organizations', params] as const,
  organization: (id: string) => ['organizations', 'one', id] as const,
  orgSubscriptions: (id: string) => ['organizations', 'one', id, 'subscriptions'] as const,
  orgActivity: (id: string) => ['organizations', 'one', id, 'activity'] as const,
  orgMembers: (id: string) => ['organizations', 'one', id, 'members'] as const,
  websites: (params: Record<string, unknown> = {}) => ['websites', params] as const,
  website: (id: string) => ['websites', 'one', id] as const,
  plans: ['plans'] as const,
  subscriptions: ['subscriptions'] as const,
  subscriptionsReport: ['reports', 'subscriptions'] as const,
  settings: (section: string) => ['settings', section] as const,
  catalogSources: ['catalog-sources'] as const,
  presets: (params: Record<string, unknown> = {}) => ['presets', params] as const,
  preset: (id: string) => ['presets', 'one', id] as const,
  sectionTypes: ['section-types'] as const,
  users: ['users'] as const,
  audit: (params: Record<string, unknown> = {}) => ['audit', params] as const,
  researchRuns: (orgId: string) => ['research', 'runs', orgId] as const,
  researchRun: (id: string) => ['research', 'run', id] as const,
  researchItems: (orgId: string, params: Record<string, unknown> = {}) => ['research', 'items', orgId, params] as const,
  researchSnapshots: (orgId: string) => ['research', 'snapshots', orgId] as const,
};

export const meQuery = queryOptions({
  queryKey: qk.me,
  queryFn: () => unwrap(api.GET('/admin/auth/me')),
  staleTime: 5 * 60_000,
});

export const dashboardQuery = (period: 'weeks' | 'months' | 'years' = 'weeks') =>
  queryOptions({
    queryKey: qk.dashboard(period),
    queryFn: async (): Promise<Dashboard> => unwrap(api.GET('/admin/dashboard', { params: { query: { period } } })),
  });

/** All organizations the caller sees (pickers, job labels; the organizations screen pages and filters on its own). */
export const organizationsListQuery = queryOptions({
  queryKey: qk.organizations({ size: 100 }),
  queryFn: () => unwrap(api.GET('/admin/organizations', { params: { query: { size: 100 } } })),
  select: (p): OrganizationSummary[] => p.items,
});

export const organizationQuery = (id: string) =>
  queryOptions({
    queryKey: qk.organization(id),
    queryFn: async (): Promise<Organization> => unwrap(api.GET('/admin/organizations/{org_id}', { params: { path: { org_id: id } } })),
  });

/** All websites the caller sees (job labels; the sites screen has its own query). */
export const websitesListQuery = queryOptions({
  queryKey: qk.websites({ size: 100 }),
  queryFn: () => unwrap(api.GET('/admin/websites', { params: { query: { size: 100 } } })),
  select: (p): Website[] => p.items,
});

/** The offered plans (latest active versions), by position: the plans screen, pickers and filters. */
export const plansQuery = queryOptions({
  queryKey: qk.plans,
  queryFn: () => unwrap(api.GET('/admin/plans', { params: { query: { size: 100 } } })),
  select: (p): Plan[] => [...p.items].sort((a, b) => a.position - b.position),
});

/** An organization's research runs, newest first (the research block, the sources card). */
export const researchRunsQuery = (orgId: string) =>
  queryOptions({
    queryKey: qk.researchRuns(orgId),
    queryFn: async (): Promise<Run[]> => unwrap(api.GET('/admin/organizations/{org_id}/research-runs', { params: { path: { org_id: orgId } } })),
  });

/** Approved research snapshots, newest first (P2-C02: the approved report, «отчёт vN»). */
export const snapshotsQuery = (orgId: string) =>
  queryOptions({
    queryKey: qk.researchSnapshots(orgId),
    queryFn: () => unwrap(api.GET('/admin/organizations/{org_id}/research-snapshots', { params: { path: { org_id: orgId } } })),
    select: (list): SnapshotOut[] => [...list].sort((a, b) => b.version - a.version),
    // Until P2-C02 is deployed the endpoint is missing: treat that as "no snapshot yet".
    retry: false,
  });

/** Queued and running jobs (the header pill's first value, before the event stream connects). */
export const runningJobsQuery = queryOptions({
  queryKey: qk.runningJobs,
  queryFn: () => unwrap(api.GET('/admin/jobs', { params: { query: { active: true, size: 50 } } })),
});
